import { useEffect } from 'react'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Switch, FormControlLabel, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createCashAccount, updateCashAccount, type CashAccount, type SaveCashAccountPayload } from '../../api/financiar'
import { errorMessage } from '../../api/client'

const schema = z.object({
  name: z.string().min(1, 'Câmp obligatoriu'),
  kind: z.enum(['Bancar', 'Casierie']),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  iban: z.string().nullable(),
  isActive: z.boolean(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: SaveCashAccountPayload = { name: '', kind: 'Bancar', currency: 'RON', iban: '', isActive: true }

export default function CashAccountFormDialog({ target, onClose }: { target: CashAccount | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ defaultValues: EMPTY, resolver: zodResolver(schema) })
  const kind = useWatch({ control, name: 'kind' })

  useEffect(() => {
    if (editing) reset({ ...(target as CashAccount) })
    else if (target === 'new') reset(EMPTY)
  }, [target, editing, reset])

  const mutation = useMutation({
    mutationFn: (values: SaveCashAccountPayload) =>
      editing ? updateCashAccount((target as CashAccount).id, values).then(() => undefined) : createCashAccount(values),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['financiar', 'conturi'] }); onClose() },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare cont' : 'Cont nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )} />
            <Controller name="kind" control={control} render={({ field }) => (
              <TextField {...field} select label="Tip">
                <MenuItem value="Bancar">Cont bancar</MenuItem>
                <MenuItem value="Casierie">Casierie numerar</MenuItem>
              </TextField>
            )} />
            <Controller name="currency" control={control} render={({ field }) => (
              <TextField {...field} label="Monedă" sx={{ width: 120 }} error={!!errors.currency} helperText={errors.currency?.message} />
            )} />
            {kind === 'Bancar' && (
              <Controller name="iban" control={control} render={({ field }) => (<TextField {...field} value={field.value ?? ''} label="IBAN" fullWidth />)} />
            )}
            <Controller name="isActive" control={control} render={({ field }) => (
              <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Activ" />
            )} />
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
